import FetchApi from './FetchApi.mjs'
import CookieApi from './CookieApi.mjs'


const WpApi = {

	
	/**
	 * Get array of products currently in cart from WP backend
	 * 
	 * @return	{array} 	Cart products
	 */	
	async getUserCart() {
		const { success, data } = await FetchApi.fetch( 'get_user_cart' )

		return success ? data : []
	},


	/**
	 * Get user metadata from WP backend
	 * 
	 * @return	{obj} 	User metadata
	 */		
	async getUserStatus() {
		const { data } = await FetchApi.fetch( 'get_user_status' )

		// defaults
		let { 
			is_guest 		= false, 
			is_logged_in 	= false, 
			is_penn_med 	= false, 
			location 		= null,
			ordertype 		= '', 
			user_name 		= '', 
		} = data

		// maybe grab location ID from location data obj
		if( 'object' === typeof location && location ) {
			if( Object.prototype.hasOwnProperty.call( location, 'id' ) ) {
				location = location.id
			}
		}

		// location should be numeric ID (fun fact, invoking isNaN on an empty string returns false)
		location = parseInt( location )
		if( isNaN( location ) ) {
			location = null
		}

		// double-check that user isn't set as guest if user is know
		if( is_logged_in || is_penn_med || user_name ) {
			is_guest = false
		}

		// refresh cookie
		// @todo — standardize key names
		CookieApi.setUserCookie({
			guest: 		is_guest,
			location: 	location,
			name: 		user_name,
			ordertype: 	ordertype,
			penn: 		is_penn_med,
		})

		return {
			displayName: 	user_name,
			isGuest: 		is_guest,
			isLoggedIn: 	is_logged_in,
			isPennMed: 		is_penn_med,
			locationId:		location,
			ordertype: 		ordertype,
		}
	},


	/**
	 * Get store status (e.g. delivery is open or not) from WP backend
	 * 
	 * @return	{obj} 	Store open/close status
	 */		
	async getStoreStatus() {
		const { success, data } = await FetchApi.fetch( 'get_store_status' )

		return success ? data : false
	},


	/**
	 * Get all products that belong to a specific category (e.g. Premium Wraps) from WP backend
	 * 
	 * @param 	{int} 	categoryId 	WordPress term ID
	 * @return	{array} Products in category
	 */	
	async getCategoryProducts( categoryId ) {
		const { success, data } = await FetchApi.fetch( 
			'get_category_products',
			{
				id: categoryId
			}
		)

		return success ? data : []
	},


	/**
	 * Add specific product to cart (and save in WP backend)
	 * 
	 * @param 	{obj} 	[ data ]	Product metadata
	 * @return	{mixed} Updated list of cart items or error
	 */	
	addProductToCart( data = {} ) {
		return new Promise( ( resolve, reject ) => {
			const { 
				addons 			= {}, 
				cart_item_key 	= null, 
				product_id, 
				quantity 		= 1, 
				variation_id 	= null
			} = data

			if( isNaN( product_id ) ) {
				return reject( 'Invalid product ID' )
			}

			const params = new URLSearchParams()
			
			// required items
			params.append( 'product_id',	product_id )
			params.append( 'quantity',		quantity )

			// required if updating a preexisting cart item
			if( 'string' === typeof cart_item_key && cart_item_key.length ) {
				params.append( 'cart_item_key', cart_item_key )
			}

			// required if adding a variation of a product
			if( !isNaN( variation_id ) ) {
				params.append( 'variation_id', variation_id )
			}

			// loop through addons (to match Woo Product Addons structure)
			for( const addonKey in addons ) {
				const addon = addons[ addonKey ]

				for( const optionKey in addon.options ) {
					const option = addon.options[ optionKey ]

					if( option.active ) {
						params.append( `${addonKey}[]`, option.key )
					}
				}
			}

			FetchApi
				.fetch( 'add_item_to_cart', params, 'post' )
				.then( response => {
					const { success, data } = response

					FetchApi.refreshNonces()
		
					if( !success || 'object' !== typeof data ) {
						console.warn( response )
						return reject( `Error occurred: ${data.toString()}. Please try again.` )

					} else {
						return resolve( data )
					}
				})
		})
	},


	/**
	 * Update quantity for specific product in cart (and save in WP backend)
	 * 
	 * @param 	{string}	cartItemHashKey	Cart item's unique identifier
	 * @return	{mixed}		Updated cart item metadata or error
	 */	
	updateItemQuantity( cartItemHashKey, quantity ) {
		return new Promise( ( resolve, reject ) => {
			if( !cartItemHashKey.length ) {
				return reject( 'Invalid cart item key' )
			}

			quantity = parseInt( quantity )
			if( isNaN( quantity ) ) {
				return reject( 'Invalid quantity' )
			}

			const params = new URLSearchParams()

			params.append( 'cart_item_key', cartItemHashKey )
			params.append( 'quantity', 		quantity )

			FetchApi
				.fetch( 'set_cart_item_quantity', params, 'post' )
				.then( response => {
					const { success, data } = response
					
					if( !success ) {
						console.warn( response )
						return reject( `Error occurred: ${data.toString()}. Please try again.` )

					} else {
						return resolve( data )
					}
				})
			
		})
	},


	/**
	 * Delete product from cart (and save in WP backend)
	 * 
	 * @param 	{string}	cartItemHashKey	Cart item's unique identifier
	 * @return	{obj}		Original cart item metadata or error
	 */	
	deleteItemFromCart( cartItemHashKey ) {
		return new Promise( ( resolve, reject ) => {
			const params = new URLSearchParams()

			params.append( 'cart_item_key', cartItemHashKey )

			FetchApi
				.fetch( 'remove_cart_item', params, 'post' )
				.then( response => {
					const { success, data } = response
		
					if( !success ) {
						console.warn( response )
						return reject( `Error occurred: ${data.toString()}. Please try again.` )

					} else {
						return resolve( data )
					}
				})
		})
	},


	/**
	 * Try to login user into WP based on provided creds
	 * 
	 * Grabbed $_POST keys from wp_signon()
	 * 
	 * @param 	{string}	username 	Username
	 * @param 	{string}	password 	Password
	 * 
	 * @return	{obj}	User meta or error (if unsuccessful in signing in)
	 */	
	submitUserLogin( username, password ) {
		const loginData = {
			log: 		username,
			pwd: 		password,
			rememberme: 1
		}

		return FetchApi.fetch( 'submit_user_login', loginData, 'post' )
	},


	/**
	 * Create new user account in WP (and automatically sign in)
	 * 
	 * @param 	{string}	firstName 		User's first name
	 * @param 	{string}	lastName 		User's last name
	 * @param 	{string}	email 			User's email address
	 * @param 	{string}	password 		Password for user account
	 * @param 	{string}	[ pennMedCode ]	Code for validating Penn Med users
	 * 
	 * @return	{obj}	User meta or error (if unsuccessful in signing in)
	 */		
	submitUserCreate( firstName, lastName, email, password, pennMedCode = '' ) {
		const accountData = {
			first_name: firstName,
			last_name: 	lastName,
			email: 		email,
			password: 	password,
			penn_med:	pennMedCode
		}

		return FetchApi.fetch( 'submit_user_create', accountData, 'post' )
	},


	/**
	 * Update order type (e.g. delivery, pickup) for user (and save in WP admin)
	 * 
	 * @param 	{string} 	ordertype 	Either "pickup" or "delivery"
	 * @param 	{string}	password 	Password
	 */		
	setUserOrderType( orderType ) {
		CookieApi.setCookieValue( 'ordertype', orderType )
		
		// todo — can't remember why I commented this out
		// const data = { 
		// 	order_type: orderType 
		// }

		// return FetchApi.fetch( 'set_user_ordertype', data, 'post' )
	},


	/**
	 * Update selected location for user (and save in WP admin)
	 * 
	 * @param 	{mixed}	locationId 	Integer, for valid location; otherwise, null
	 * @return	{obj}	User meta or error
	 */			
	setUserLocationId( locationId ) {
		CookieApi.setCookieValue( 'location', locationId )

		const data = { 
			location_id: locationId 
		}

		return FetchApi.fetch( 'set_user_location', data, 'post' )
	},


	/**
	 * Check if delivery is an available order type from WP admin
	 * 
	 * @return	{bool}	True, if delivery is available
	 */			
	async checkDeliveryClosedStatus() {
		const { success, data } = await FetchApi.fetch( 'get_delivery_closed_status' )

		return success ? data : false
	},	


	/**
	 * Grab pertinent data from content saved to page via wp_localize_script
	 * 
	 * @return	{obj} 	Data to get app working
	 */			
	extractAppData() {
		const wpData = window.FxOnlineStore,
			appData = {}

		appData.categories = {}
		appData.extras = {}
		appData.links = wpData.store.links
		// appData.locations = wpData.store.locations
		appData.messages = wpData.store.messages
		appData.pennMedLogo = wpData.store.penn_med_logo
		appData.googleApiKey = wpData.store.google_api_key
		
		/**
		 * Moved from beforeMount to FxOnlineStore variable. This will avoid message flickering (e.g. when store is 
		 * open and shows open message, but then store is closed as a result of getStoreStatus fetch in beforeMount)
		 * 
		 * todo — if Kinsta re-enables caching, we'll need to revert to previous implementation with status fetch in 
		 * beforeMount
		 */
		appData.storeStatus = wpData.store.status
	
		wpData.store.categories.forEach( category => {
			const { id, title, img, description = '' } = category
		
			appData.categories[`category_${id}`] = {
				id: 			id,
				title: 			title,
				imageUrl: 		img,
				products: 		[],
				description: 	description,
			}
		})

		appData.locations = []
		wpData.store.locations.forEach( location => {
			appData.locations.push({
				isSelected: false,
				...location
			})
		})
		
		wpData.store.extras.forEach( extra => {
			appData.extras[`extra__${extra.id}`] = extra
		})

		// grab userdata from custom cookie
		const cookie = CookieApi.getUserCookie()

		const { guest = false, penn = false, name = '' } = cookie

		appData.user = {
			displayName:	name,
			isGuest: 		( guest && !name.length ),
			isPennMed: 		( penn && name.length ),
		}
	
		return appData
	},


	/**
	 * Get updated nonces in case they expired (or old)
	 * 
	 * @return	{obj} 	Nonces, keyed by action
	 */			
	refreshNonces() {
		return new Promise( resolve => {
			FetchApi
				.refreshNonces()
				.then( result => resolve( result ) )
		})
	},

}


export default WpApi